<script lang="ts">
import { defineComponent, PropType, ref, computed, watch, onMounted, nextTick } from 'vue'
import { appOrigin, isValidURL } from '@/utilities/url'
import { formatToUpper } from '@/utilities/formatter'

export default defineComponent({
  name: 'SearchForm',

  props: {
    /**
     * input の value
     */
    modelValue: {
      type: String,
      default: undefined,
    },

    /**
     * input の name
     */
    name: {
      type: String,
      default: undefined,
    },

    /**
     * フォーム要素情報
     */
    form: {
      type: Object as PropType<Form>,
      required: true,
    },

    /**
     * フォームの入力エリアに表示する placeholder 文字列
     */
    placeholder: {
      type: String,
      default: '',
    },

    /**
     * フォームのアクションに渡すページ URL のクエリパラメータのうち、除外するクエリ key 名リスト
     */
    ignoreQueries: {
      type: Array as PropType<string[]>,
      default: () => [],
    },

    /**
     * フォームのサイズ
     */
    size: {
      type: String as PropType<'small' | 'normal' | 'large'>,
      default: 'normal',
    },

    /**
     * フォームの入力エリアの色
     */
    color: {
      type: String as PropType<'white' | 'gray'>,
      default: 'white',
    },

    /**
     * 検索リセットボタンを置くかどうか
     */
    hasReset: {
      type: Boolean,
      default: false,
    },

    /**
     * 入力フォームを disabled にするかどうか
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * 入力フォームを readonly にするかどうか
     */
    readonly: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'focus', 'blur', 'click:form'],

  setup(props, { emit }) {
    const formEl = ref()
    const localValue = ref('')
    const initialValue = ref('')
    const isHoverSearchButton = ref(false)

    /**
     * ワード検索の action 属性に付与するパス内容
     * @description サーバから受け取ったパス情報に、資料フィルターのクエリパラメータ内容を付与した文字列にする
     */
    const searchActionPath = computed<string>(() => {
      // form の method が「get」の場合、HTML の仕様上 action のパス情報に含まれるクエリパラメータは無視されるため、
      // サーバから渡されたパス情報をそのまま返す（クエリパラメータの付与工程を省く）
      // ※ GET メソッドの form の場合、パラメータは hidden で送ること
      // @see https://otiai10.hatenablog.com/entry/2022/10/10/102718
      if (formatToUpper(props.form.method) === 'GET') return props.form.action

      const pageUrl = new URL(location.href)

      // 除外リストのクエリはページ URL のクエリパラメータから削除
      props.ignoreQueries.forEach(v => {
        pageUrl.searchParams.delete(v)
      })

      const isURL = isValidURL(props.form.action)

      try {
        const actionUrl = isURL
          ? new URL(props.form.action)
          : new URL(appOrigin() + props.form.action)

        // 一旦 form のパスに入っているクエリパラメータのうち、ページ URL に含まれているものと重複するものはすべて除外
        for (const key of pageUrl.searchParams.keys()) {
          actionUrl.searchParams.delete(key)
        }

        // ページ URL に含まれるクエリパラメータをフォームのパスにもすべて付与
        // ?foo=bar,baz のような配列系クエリパラメータは、Rails Way に則り、?foo[]=bar&foo[]=baz というクエリとして処理するため、
        // 同一複数のクエリ key が発生する。その場合、set だと重複除外されるため、append にする。
        pageUrl.searchParams.forEach((value, key) => {
          actionUrl.searchParams.append(key, value)
        })

        // もともとサーバ側から受け取ったパス情報が URL 形式だった場合はそのままバインドし、URL 形式でない場合はこちら側で一時的に整形した URL のため、パス部分だけ切り出してバインド
        const path = isURL
          ? actionUrl.toString()
          : actionUrl.toString().replace(actionUrl.origin, '')

        return path
      } catch (e) {
        return props.form.action
      }
    })

    /**
     * 検索リセットボタンを表示するかどうか
     * @description 未検索状態（＝初期描画状態で検索ワードが渡ってきていない）ではリセットボタンは表示しない
     */
    const isShowSeachReset = computed<boolean>(() => props.hasReset && !!formEl.value && !!initialValue.value)

    const sizeVariable = computed<{ height: string, fontSize: string }>(() => {
      const FONT_SIZE_PX_AS_SMALL = 10
      const FONT_SIZE_PX_AS_NORMAL = 14
      const FONT_SIZE_PX_AS_LARGE = 18

      const FORM_HEIGHT_PX_AS_SMALL = 30
      const FORM_HEIGHT_PX_AS_NORMAL = 38
      const FORM_HEIGHT_PX_AS_LARGE = 42

      const formHeight = props.size === 'small'
        ? `${FORM_HEIGHT_PX_AS_SMALL / FONT_SIZE_PX_AS_SMALL}em`
        : props.size === 'large'
          ? `${FORM_HEIGHT_PX_AS_LARGE / FONT_SIZE_PX_AS_LARGE}em`
          : `${FORM_HEIGHT_PX_AS_NORMAL / FONT_SIZE_PX_AS_NORMAL}em`

      const fontSize = props.size === 'small'
        ? `${FONT_SIZE_PX_AS_SMALL / 10}rem`
        : props.size === 'large'
          ? `${FONT_SIZE_PX_AS_LARGE / 10}rem`
          : `${FONT_SIZE_PX_AS_NORMAL / 10}rem`

      return {
        height: formHeight,
        fontSize: fontSize,
      }
    })

    watch(() => props.modelValue, () => {
      if (props.modelValue !== undefined) {
        localValue.value = props.modelValue
      }
    })

    onMounted(() => {
      // 外から input のデータが bind された場合のみ初期設定値を代入する
      if (props.modelValue) {
        localValue.value = props.modelValue || ''
        initialValue.value = props.modelValue || ''
      }
    })

    const reset = () => {
      if (!isShowSeachReset.value) return

      localValue.value = ''
      emit('update:modelValue', localValue.value)

      nextTick(() => {
        formEl.value.submit()
      })
    }

    return {
      // data
      formEl,
      localValue,
      initialValue,
      isHoverSearchButton,

      // computed
      searchActionPath,
      isShowSeachReset,
      sizeVariable,

      // methods
      reset,
    }
  }
})
</script>

<template>
  <form
    ref="formEl"
    class="form"
    :action="searchActionPath"
    :method="form.method"
    @click="$emit('click:form')">
    <div
      class="l-unit__searchForm"
      :style="{
        '--form-height': sizeVariable.height,
        '--font-size': sizeVariable.fontSize,
      }">
      <div class="l-unit__searchForm--description">
        <div
          :class="[
            'o-input__searchForm',
            `is-${color}`,
          ]"
          :style="{
            '--form-height': sizeVariable.height,
            '--font-size': sizeVariable.fontSize,
          }">
          <input
            v-model="localValue"
            class="o-input__searchForm--item"
            type="text"
            :name="name"
            :placeholder="placeholder"
            autocomplete="off"
            :disabled="disabled"
            :readonly="readonly"
            @update:model-value="$emit('update:modelValue', localValue)"
            @focus="$emit('focus')"
            @blur="$emit('blur')">
        <!-- /.o-input__searchForm --></div>

        <slot />
      <!-- /.l-unit__searchForm description --></div>

      <!--
        検索フォームが連続した際に、
        hover してもツールチップが後続要素の下に隠れてしまう問題を回避するために、
        hover 時にのみ（hover 時のみにしないと後続の検索フォームも z-index が指定されることになり、問題が解決しないので）、
        親の action ラッパーに z-index を明示的に指定し、レイヤー順序を制御する。

        また、SearchWord.vue のサジェスト表示時に検索ボタンを hover した際、
        レンダリング順の関係で検索ボタンのツールチップが隠れてしまうため、
        値はサジェストの z-index 値（1）より明示的に大きい値（2）とする。
      -->
      <div
        class="l-unit__searchForm--action is-submit"
        :style="{ 'z-index': isHoverSearchButton ? 2 : undefined }">
        <div
          :class="[
            'o-action__searchForm',
            `is-${size}`,
          ]">
          <button
            :class="[
              'o-action__searchForm--item',
              { 'u-action__tooltip': !disabled },
            ]"
            type="submit"
            :disabled="disabled"
            :readonly="readonly"
            data-tooltip="検索する"
            @mouseenter="isHoverSearchButton = true"
            @mouseleave="isHoverSearchButton = false">
            検索
          </button>
        <!-- /.o-action__searchForm --></div>
      <!-- /.l-unit__searchForm action --></div>

      <div
        v-if="isShowSeachReset"
        class="l-unit__searchForm--action is-reset">
        <div class="o-action__reset">
          <button
            :class="[
              'o-action__reset--item',
              { 'u-action__tooltip': initialValue },
            ]"
            type="reset"
            :disabled="!initialValue"
            data-tooltip="検索ワードをリセット"
            @click.prevent="reset">
            リセット
          </button>
        <!-- /.o-action__reset --></div>
      <!-- /.l-unit__searchForm action --></div>
    <!-- /.l-unit__searchForm --></div>
  </form>
</template>

<style lang="sass" scoped>
$font-size-px: $font-small-px
$font-size: #{math.div($font-size-px, 10)}rem
$form-height-px: 38
$form-height: changeSizePx2Em($form-height-px, $font-size-px)
$form-border-size: 1px

// layout
.l-unit__searchForm
  // 実際の値は props の size から computed で算出
  --form-height: $form-height
  --font-size: $font-size
  $root: &

  position: relative
  display: grid
  grid-template-columns: 1fr auto auto
  grid-template-rows: auto
  align-items: center

  & &--description
    grid-column: 1 / 3
    grid-row: 1

  & &--action
    &.is-submit
      width: var(--form-height)
      height: var(--form-height)
      grid-column: 2 / 3
      grid-row: 1
      font-size: var(--font-size)
      padding: 0.3em

    &.is-reset
      height: 100%
      grid-row: 1
      margin-left: 0.3em

// object
.o-input__searchForm
  $root: &
  --form-height: $form-height
  --font-size: $font-size
  $button-area-size: var(--form-height)

  & &--item
    width: 100%
    line-height: var(--form-height)
    color: rgba($font-black, 0.87)
    font-size: var(--font-size)
    padding: 0 $button-area-size 0 1.2em
    background-color: rgba($bg-white, 1)
    border: $form-border-size solid $border-x-light-gray
    border-radius: 100vh
    +transition

    &::placeholder
      color: rgba($font-black, 0.38)

    +mouseover
      border: 1px solid $border-dark-gray

    &:focus
      background-color: $bg-white
      border: 1px solid $border-dark-orange

  &.is-gray
    #{$root}--item
      background-color: rgba($bg-x-light-gray, 1)

  +breakpoint
    #{$root}--item
      border: 1px solid $bg-dark-orange

    &.is-gray
      #{$root}--item
        background-color: $bg-white

.o-action__searchForm
  $root: &
  height: 100%

  & &--item
    width: 100%
    height: 100%
    display: block
    color: rgba($font-black, 0)
    line-height: 100%
    font-size: $font-xx-small // for tooltip
    white-space: nowrap
    background:
      image: url(@/images/icon/icon-search_01.svg)
      repeat: no-repeat
      position: center center
    border-radius: 50%
    +transition($duration: $transition-long)

    &:disabled
      opacity: 0.5

    &:not(:disabled):hover
      background:
        color: rgba($bg-orange, 0.2)
        image: url(@/images/icon/icon-search_02.svg)

  &.is-small
    #{$root}--item
      background-size: 110%

  &.is-normal
    #{$root}--item
      background-size: 120%

  &.is-large
    #{$root}--item
      background-size: 140%

.o-action__reset
  $root: &
  $reset-button-font-size: calc(var(--font-size) * 0.7)
  height: 100%

  & &--item
    width: 100%
    height: 100%
    display: block
    line-height: 100%
    color: $font-light-gray
    font-size: $reset-button-font-size
    padding: 0 0.7em
    border: $form-border-size solid $border-x-light-gray
    border-radius: 100vh
    white-space: nowrap
    +transition

    &:not(:disabled)
      +mouseover
        background-color: $bg-white
        border: 1px solid $bg-dark-orange

    &:disabled
      opacity: 0.5

  &.is-gray
    #{$root}--item
      background-color: rgba($bg-x-light-gray, 1)

  +breakpoint
    &.is-gray
      #{$root}--item
        background-color: $bg-white
</style>
